import { AuthLayout, Button, Field, Input, errorMessage } from '@bixo/ui'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { getRouteApi, Link, useNavigate } from '@tanstack/react-router'
import { AlertCircle, KeyRound } from 'lucide-react'
import { useState, type FormEvent } from 'react'
import { tenantBadges, tenantFeatures } from '../components/auth-brand'
import { TenantShowcase } from '../components/auth-showcase'
import { endpoints, qk, tenantQuery } from '../lib/api'

const route = getRouteApi('/two-factor')

export function TwoFactorPage() {
  const { data: tenant } = useQuery(tenantQuery)
  const { redirect } = route.useSearch()
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const [useRecovery, setUseRecovery] = useState(false)
  const [value, setValue] = useState('')
  const [error, setError] = useState<string | null>(null)

  const challenge = useMutation({
    mutationFn: endpoints.twoFactorChallenge,
    onSuccess: async ({ user }) => {
      queryClient.setQueryData(qk.me, user)
      await navigate({ to: redirect ?? '/' })
    },
    onError: (err) => {
      setError(errorMessage(err, 'That code did not work. Try again.'))
      setValue('')
    },
  })

  const submit = (event: FormEvent): void => {
    event.preventDefault()
    setError(null)
    const trimmed = value.trim()
    if (useRecovery ? trimmed.length < 8 : !/^\d{6}$/.test(trimmed)) {
      setError(useRecovery ? 'Enter one of your recovery codes.' : 'Enter the 6-digit code from your app.')
      return
    }
    challenge.mutate(useRecovery ? { recovery_code: trimmed } : { code: trimmed })
  }

  const name = tenant?.app_name ?? tenant?.name ?? 'Bixo'

  return (
    <AuthLayout
      brand={{ name, logoUrl: tenant?.logo_url ?? null }}
      title="Two-step verification"
      subtitle={
        useRecovery
          ? 'Enter one of the recovery codes you saved when you turned on two-factor sign-in.'
          : 'Open your authenticator app and enter the 6-digit code for this account.'
      }
      showcase={
        <TenantShowcase eyebrow="Two-step verification" title="One more step keeps your books safe." />
      }
      features={tenantFeatures}
      badges={tenantBadges}
    >
      <form onSubmit={submit} className="grid gap-5" noValidate>
        {error && (
          <div
            role="alert"
            className="flex items-start gap-2.5 rounded-lg border border-danger/25 bg-danger-soft px-3 py-2.5 text-sm text-danger"
          >
            <AlertCircle className="mt-0.5 size-4 shrink-0" />
            {error}
          </div>
        )}

        <Field label={useRecovery ? 'Recovery code' : 'Authentication code'}>
          {(control) =>
            useRecovery ? (
              <Input
                {...control}
                key="recovery"
                autoFocus
                autoComplete="off"
                spellCheck={false}
                placeholder="abcde-12345"
                className="tabular h-12 font-mono tracking-wider"
                value={value}
                onChange={(e) => setValue(e.target.value)}
              />
            ) : (
              <Input
                {...control}
                key="totp"
                autoFocus
                inputMode="numeric"
                autoComplete="one-time-code"
                maxLength={6}
                placeholder="000000"
                className="tabular h-14 text-center font-mono text-2xl tracking-[0.6em]"
                value={value}
                onChange={(e) => setValue(e.target.value.replace(/\D/g, '').slice(0, 6))}
              />
            )
          }
        </Field>

        <Button type="submit" size="lg" loading={challenge.isPending} className="w-full">
          Verify and continue
        </Button>

        <div className="flex flex-col items-center gap-2 text-sm">
          <button
            type="button"
            onClick={() => {
              setUseRecovery((v) => !v)
              setValue('')
              setError(null)
            }}
            className="inline-flex cursor-pointer items-center gap-1.5 font-medium text-primary hover:underline"
          >
            <KeyRound className="size-3.5" />
            {useRecovery ? 'Use your authenticator app instead' : 'Lost your phone? Use a recovery code'}
          </button>
          <Link to="/login" className="text-muted-foreground hover:text-foreground">
            Back to sign in
          </Link>
        </div>
      </form>
    </AuthLayout>
  )
}
